import { getOneAccount } from "../../accounts";
import { JsonResp, unwrapJsonResp } from "../../JsonResp";
import {
  getUserFriendlyErrorMessage,
  UserFriendlyError,
} from "../../UserFriendlyError";
import axios from "axios";
import { AccountType } from "../../../shared/types/accounts";
import {
  instagramProfileCandidate,
  matchAccountIdentity,
} from "../../../entities/accounts/libs/account-identity";
import { FacebookScopes, getFacebookAdsScopes } from "../facebook";
import {
  authoriseFacebook,
  ConnectionHandlePageBody,
  FacebookConnectSession,
  isConnectionHandleSession,
  loginArgs,
  toInstagramPickerPage,
} from "../facebook/authorise-facebook";
import {
  type BusinessTokenType,
  FBPageWithInsta,
  fbReadInstaBizAccount,
  fbReadMe,
  fbReadPages,
  getFacebookAuthType,
} from "../facebook/fb-sdk-utils";
import { toast } from "react-toastify";
import { selectCurrentUser } from "../../../entities/current-user";
import { resolveReduxAppStore } from "../../../entities/lib/redux-app-store";
import {
  InstagramScopeInboxComments,
  InstagramScopeProductTagging,
  InstagramScopePublishing,
  preservedInstagramScopes,
  RequiredInstagramScopes,
} from "./scopes";
import { adminPanelFeatureToggle } from "../../../utils/feature-toggle-utils";

export type {
  FBPageWithInsta,
  InstaAccount,
  FBPage,
} from "../facebook/fb-sdk-utils";

export * from "./posting";

export * from "./scopes";

export async function getInstagramAccounts(
  {
    additionalScopes,
    useShortPagesFbApi,
    businessTokenType,
  }: {
    additionalScopes: string[];
    useShortPagesFbApi: boolean;
    /** SKE-5310. `user` when the person chose "Connect another way". */
    businessTokenType?: BusinessTokenType;
  } = { additionalScopes: [], useShortPagesFbApi: false }
) {
  // SKE-4526. The publishing consent is per-admin, so an account only holds
  // instagram_content_publish where the rollout put it. Inbox comments are
  // spread separately and stay ungated; the Set absorbs the overlap with
  // InstagramScopePublishing. With the reconnect campaign on, a first connect
  // asks for the same publishing set the campaign pass does, so a fresh row
  // does not land on "needs update".
  const requestPublishScopes =
    adminPanelFeatureToggle.syncGet("enableInstagramPublishScopeRequest") ||
    adminPanelFeatureToggle.syncGet("instagramReconnectCampaign");
  const scopes = Array.from(
    new Set([
      ...RequiredInstagramScopes,
      ...InstagramScopeInboxComments,
      ...(requestPublishScopes
        ? [...InstagramScopePublishing, ...InstagramScopeProductTagging]
        : []),
      ...additionalScopes,
      ...FacebookScopes,
      ...getFacebookAdsScopes(),
    ])
  );
  try {
    const appStore = resolveReduxAppStore();
    const user = appStore
      ? selectCurrentUser(appStore.getState() as never)
      : undefined;

    useShortPagesFbApi =
      user?.preferences?.useShortPagesFbApi || useShortPagesFbApi || false;
    console.log("Doing FB Login");
    const authorisation = await authoriseFacebook(
      ...loginArgs(scopes, businessTokenType)
    );
    if (authorisation.kind === "handle") {
      if (authorisation.pages.length === 0) {
        throw new UserFriendlyError(
          "Facebook did not return any Instagram accounts when you connected. Are you logged into the right Facebook account?"
        );
      }
      const session: FacebookConnectSession = {
        connectionHandle: authorisation.connectionHandle,
      };
      return {
        session,
        pages: authorisation.pages.map(toInstagramPickerPage),
      };
    }
    const auth = authorisation.auth;
    console.log("FB login complete, reading me");
    await fbReadMe(auth);
    console.log("Completed read me, reading pages for IG");
    if (useShortPagesFbApi)
      console.log(
        "Using shorter pagination limit due to useShortPagesFbApi boolean being true."
      );
    const fb_accounts = await fbReadPages(auth, useShortPagesFbApi);
    console.log("FB pages for IG read", fb_accounts);
    // check if all FB accounts
    // TODO: ensure we really need this call
    if (fb_accounts.length === 0) {
      console.log(
        "Facebook did not return any Instagram accounts for this user.",
        fb_accounts
      );
      throw new UserFriendlyError(
        "Facebook did not return any Instagram accounts when you connected. Are you logged into the right Facebook account?"
      );
    }
    const pages = await Promise.all(
      // used to be acc,auth,fb_accounts??? but fb_accounts isn't used in the function
      fb_accounts.map((acc) => fbReadInstaBizAccount(acc, auth))
    );
    const session: FacebookConnectSession = auth;
    return {
      session,
      pages,
    };
  } catch (e) {
    console.error(e);
    toast.error(getUserFriendlyErrorMessage(e as Error));
    throw e;
  }
}

export async function addInstagramAccount({
  page,
  session,
}: {
  page: FBPageWithInsta;
  session: FacebookConnectSession;
}): Promise<AccountType> {
  if (isConnectionHandleSession(session)) {
    const handleBody: ConnectionHandlePageBody = {
      connectionHandle: session.connectionHandle,
      pageId: page.page.id,
    };
    const handleResponse = await axios.request<AccountType>({
      method: "post",
      url: "/accounts/instagram",
      data: handleBody,
    });
    return handleResponse.data;
  }
  const response = await axios.request<AccountType>({
    method: "post",
    url: "/accounts/instagram",
    data: {
      session,
      facebookAuthType: getFacebookAuthType(),
      userToken: session.accessToken,
      user_token: session.accessToken,
      ...page.page,
      igData: page.ig,
    },
  });
  return response.data;
}

export async function addInstagramAPIkey(
  accountId: string,
  {
    session,
    page,
  }: { session: FacebookConnectSession; page: Required<FBPageWithInsta> }
): Promise<AccountType> {
  if (isConnectionHandleSession(session)) {
    const handleBody: ConnectionHandlePageBody = {
      connectionHandle: session.connectionHandle,
      pageId: page.page.id,
    };
    const handleResponse = await axios.request<JsonResp<string>>({
      method: "put",
      url: `/accounts/addIgToken/${accountId}`,
      data: handleBody,
    });
    unwrapJsonResp(handleResponse.data);
    return await getOneAccount(accountId);
  }
  const response = await axios.request<JsonResp<string>>({
    method: "put",
    url: `/accounts/addIgToken/${accountId}`,
    data: {
      igData: {
        ...page.ig,
        access_token: page.page.access_token,
        facebookAuthType: getFacebookAuthType(),
        user_token: session.accessToken,
        userToken: session.accessToken,
        associatedPageId: page.page.id,
      },
    },
  });

  unwrapJsonResp(response.data);

  return await getOneAccount(accountId);
}

/**
 * Reconnect an Instagram business account through Facebook Login.
 *
 * Boosting uses the Facebook user token, while the Instagram account itself
 * still needs its page token.  Keep this flow beside the existing Instagram
 * connect helpers so the generic Manage Accounts reconnect action can request
 * both token types and persist the resulting Ads capability state.
 */
export async function reconnectInstagramAccount(
  account: AccountType
): Promise<AccountType> {
  /*
   * SKE-4806. This called `getInstagramAccounts()` with no arguments, so
   * whenever the admin toggle was off it asked for no publishing permissions
   * even when this very account held them. Since `addInstagramAPItoken`
   * replaces the stored session wholesale, that was not a smaller ask, it was a
   * downgrade of the account being reconnected.
   *
   * Only the publishing permissions need topping up here. `instagram_manage_
   * comments` is already in `getInstagramAccounts`'s unconditional base set,
   * which is why the siblings passing it explicitly are belt and braces rather
   * than the thing that puts it on the request.
   */
  const { session, pages } = await getInstagramAccounts({
    additionalScopes: preservedInstagramScopes([account]),
    useShortPagesFbApi: false,
  });
  const matches = pages.map((candidate) =>
    candidate.ig
      ? matchAccountIdentity(account, instagramProfileCandidate(candidate.ig))
      : null
  );
  const page = matches.includes("id")
    ? pages[matches.indexOf("id")]
    : pages[matches.indexOf("username")];

  if (!page?.ig) {
    throw new UserFriendlyError(
      `Facebook did not return the Instagram account ${account.login}. Check that you selected the correct Facebook account and granted access to its connected Page.`
    );
  }

  return addInstagramAPIkey(account._id, {
    session,
    page: page as Required<FBPageWithInsta>,
  });
}
